import { Switcher } from '@shared/ui/kit';
import type { DatePickerInputStatus } from '@shared/ui/kit/date-picker/model/types';
import { Select, type SelectValue } from '@shared/ui/select';
import { Alert, ALERT_VARIANTS } from '@features/modal-invitation/ui/alert';
import { INVITATION_CHANNEL_SELECT_OPTIONS } from '../../lib/invitation-channel-options';
import { InvitationDateField } from './components/invitation-date-field';
import { InvitationTextField } from './components/invitation-text-field';
import { InviteAcceptanceField } from './components/invite-acceptance-field';
import { InvitationPreview } from './components/invitation-preview';
import { EmailSubjectField } from '../shared/email-subject-field';
import styles from './invitation-settings-section.module.scss';

export interface InvitationSettingsValues {
  invitationExpiresAt: Date | null;
  selectedInvitationChannels: SelectValue[];
  isSmsInvitationMode: boolean;
  isBothInvitationMode: boolean;
  invitationSmsText: string;
  invitationTextParts: number;
  invitationText: string;
  invitationSubject: string;
  isEmailInvitationMode: boolean;
  inviteAcceptionInfo: string;
  isOutlookMeetingEnabled: boolean;
  previewLink: string;
  warnings: string[];
}

export interface InvitationSettingsValidation {
  invitationDatePickerStatus?: DatePickerInputStatus;
  invitationDatePickerInputMessage?: string;
  invitationDateReadOnly?: boolean;
  inviteAcceptionInfoReadOnly?: boolean;
  outlookMeetingReadOnly?: boolean;
  invitationTypeDropdownError?: string | null;
  invitationTextBodyError?: string | null;
  invitationBottomAlertText?: string | null;
}

export interface InvitationSettingsActions {
  onInvitationDateChange: (dates: [Date | null, Date | null]) => void;
  onInvitationCalendarVisibilityChange?: (open: boolean) => void;
  onInvitationChannelsChange: (values: SelectValue | SelectValue[]) => void;
  onInvitationSubjectChange: (value: string) => void;
  onInvitationTextChange: (value: string) => void;
  onInviteAcceptionInfoChange: (value: string) => void;
  onOutlookMeetingToggle: () => void;
}

interface InvitationSettingsSectionProps {
  values: InvitationSettingsValues;
  validation: InvitationSettingsValidation;
  actions: InvitationSettingsActions;
}

export const InvitationSettingsSection = ({ values, validation, actions }: InvitationSettingsSectionProps) => {
  const {
    invitationExpiresAt,
    selectedInvitationChannels,
    isSmsInvitationMode,
    isBothInvitationMode,
    invitationSmsText,
    invitationTextParts,
    invitationText,
    invitationSubject,
    isEmailInvitationMode,
    inviteAcceptionInfo,
    isOutlookMeetingEnabled,
    previewLink,
    warnings,
  } = values;

  const {
    invitationDatePickerStatus = 'default',
    invitationDatePickerInputMessage,
    invitationDateReadOnly = false,
    inviteAcceptionInfoReadOnly = false,
    outlookMeetingReadOnly = false,
    invitationTypeDropdownError,
    invitationTextBodyError,
    invitationBottomAlertText,
  } = validation;

  const {
    onInvitationDateChange,
    onInvitationCalendarVisibilityChange,
    onInvitationChannelsChange,
    onInvitationSubjectChange,
    onInvitationTextChange,
    onInviteAcceptionInfoChange,
    onOutlookMeetingToggle,
  } = actions;

  return (
    <section className={styles.settingsCard}>
      <h2 className={styles.settingsCardTitle}>Настройка приглашения</h2>
      <div className={styles.settingsCardContent}>
        <div className={styles.settingsCardFormBlock}>
          <InvitationDateField
            invitationExpiresAt={invitationExpiresAt}
            warnings={warnings}
            invitationDateReadOnly={invitationDateReadOnly}
            invitationDatePickerStatus={invitationDatePickerStatus}
            invitationDatePickerInputMessage={invitationDatePickerInputMessage}
            onInvitationDateChange={onInvitationDateChange}
            onInvitationCalendarVisibilityChange={onInvitationCalendarVisibilityChange}
          />

          <div className={styles.invitationTypeField}>
            <Select
              label="Тип отправки приглашения"
              placeholder="Выбрать тип"
              options={INVITATION_CHANNEL_SELECT_OPTIONS}
              selectedValue={selectedInvitationChannels}
              onChange={onInvitationChannelsChange}
              isWithChips
            />
            {invitationTypeDropdownError && <span className={styles.fieldError}>{invitationTypeDropdownError}</span>}
          </div>

          {isEmailInvitationMode && (
            <EmailSubjectField
              label="Тема приглашения"
              subject={invitationSubject}
              onSubjectChange={onInvitationSubjectChange}
            />
          )}

          <InvitationTextField
            isSmsInvitationMode={isSmsInvitationMode}
            isBothInvitationMode={isBothInvitationMode}
            invitationSmsText={invitationSmsText}
            invitationTextParts={invitationTextParts}
            invitationText={invitationText}
            invitationTextBodyError={invitationTextBodyError}
            onInvitationTextChange={onInvitationTextChange}
          />

          <InviteAcceptanceField
            inviteAcceptionInfo={inviteAcceptionInfo}
            inviteAcceptionInfoReadOnly={inviteAcceptionInfoReadOnly}
            onInviteAcceptionInfoChange={onInviteAcceptionInfoChange}
          />

          <div className={styles.outlookMeetingRow}>
            <div className={styles.outlookMeetingText}>Отправить встречу в Outlook после принятия приглашения</div>
            <div className={styles.outlookMeetingSwitcher}>
              <Switcher
                size="small"
                isActive={isOutlookMeetingEnabled}
                isDisabled={outlookMeetingReadOnly}
                changeFunction={onOutlookMeetingToggle}
              />
            </div>
          </div>
        </div>
        <InvitationPreview
          isSmsInvitationMode={isSmsInvitationMode}
          invitationText={invitationText}
          previewLink={previewLink}
        />
      </div>
      {invitationBottomAlertText && (
        <div className={styles.alertsWrap}>
          <Alert variant={ALERT_VARIANTS.WARNING}>{invitationBottomAlertText}</Alert>
        </div>
      )}
    </section>
  );
};
